import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { formatPhoneInput } from '../utils/phone';

const CALENDAR_BLUE = '#539c98';

function parseAddressParts(rawAddress = '') {
  const value = String(rawAddress || '').trim();
  if (!value) {
    return { addressLine: '', city: '', zip: '' };
  }

  const pieces = value.split(',').map(part => part.trim()).filter(Boolean);
  if (pieces.length >= 2) {
    const addressLine = pieces.slice(0, pieces.length - 1).join(', ');
    const lastSegment = pieces[pieces.length - 1] || '';
    const zipMatch = lastSegment.match(/(\d{5}(?:-\d{4})?)\s*$/);
    const zip = zipMatch ? zipMatch[1] : '';
    const city = zipMatch ? lastSegment.slice(0, zipMatch.index).trim() : lastSegment;
    return { addressLine, city, zip };
  }

  const zipMatch = value.match(/(\d{5}(?:-\d{4})?)\s*$/);
  if (zipMatch) {
    const zip = zipMatch[1];
    const front = value.slice(0, zipMatch.index).trim();
    return { addressLine: front, city: '', zip };
  }

  return { addressLine: value, city: '', zip: '' };
}

function composeAddress(addressLine = '', city = '', zip = '') {
  const line = String(addressLine || '').trim();
  const cityPart = String(city || '').trim();
  const zipPart = String(zip || '').trim();
  const cityZip = [cityPart, zipPart].filter(Boolean).join(' ');
  return [line, cityZip].filter(Boolean).join(', ');
}

function parseJwt(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (_err) {
    return null;
  }
}

function TenantPortal({ token }) {
  const location = useLocation();
  const tokenPayload = parseJwt(token);
  const userRole = String(tokenPayload?.role || '').toLowerCase();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    slug: '',
    address: '',
    phone: '',
    email: '',
    stripe_account_id: ''
  });
  const [addressLine, setAddressLine] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [coaRows, setCoaRows] = useState([]);
  const [coaLoading, setCoaLoading] = useState(false);
  const [coaCreating, setCoaCreating] = useState(false);
  const [coaSavingId, setCoaSavingId] = useState(null);
  const [coaDeletingId, setCoaDeletingId] = useState(null);
  const [coaError, setCoaError] = useState('');
  const [newCoa, setNewCoa] = useState({ description: '', price: '', sort_order: '' });
  const [selectedCoaId, setSelectedCoaId] = useState('');
  const [stripeStarting, setStripeStarting] = useState(false);

  const canManageCoas = userRole === 'admin' || userRole === 'office';

  const smallBlueButtonStyle = {
    background: CALENDAR_BLUE,
    color: 'white',
    border: 'none',
    borderRadius: 4,
    padding: '6px 12px',
    fontSize: 13,
    lineHeight: 1.2,
    cursor: 'pointer',
    width: 'fit-content'
  };

  const textInputStyle = {
    width: '100%',
    boxSizing: 'border-box'
  };

  const portalInnerBoxStyle = {
    border: '1px solid #ddd',
    borderRadius: 6,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    height: '100%',
    boxSizing: 'border-box'
  };

  const sectionTitleStyle = {
    margin: '0 0 10px 0'
  };

  async function loadProfile() {
    setLoading(true);
    try {
      const res = await axios.get('/tenant/me/profile', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const nextProfile = res.data || {};
      setProfile({
        ...nextProfile,
        phone: formatPhoneInput(nextProfile.phone || '')
      });
      const parts = parseAddressParts(nextProfile.address || '');
      setAddressLine(parts.addressLine);
      setCity(parts.city);
      setZip(parts.zip);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load tenant profile');
    } finally {
      setLoading(false);
    }
  }

  async function loadCoas() {
    if (!canManageCoas) return;
    setCoaLoading(true);
    setCoaError('');
    try {
      const res = await axios.get('/job-coas', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      const normalizedRows = rows.map(row => ({
        ...row,
        description: String(row.description || ''),
        price: row.price === null || row.price === undefined ? '' : String(row.price),
        sort_order: row.sort_order === null || row.sort_order === undefined ? '' : String(row.sort_order)
      }));
      setCoaRows(normalizedRows);
      setSelectedCoaId(prev => {
        if (normalizedRows.length === 0) return '';
        if (normalizedRows.some(row => String(row.coa_id) === String(prev))) return prev;
        return String(normalizedRows[0].coa_id);
      });
    } catch (err) {
      setCoaError(err?.response?.data?.error || 'Failed to load job COAs');
      setCoaRows([]);
    } finally {
      setCoaLoading(false);
    }
  }

  function handleCoaFieldChange(coaId, field, value) {
    setCoaRows(prev => prev.map(row => (row.coa_id === coaId ? { ...row, [field]: value } : row)));
  }

  async function handleSaveCoa(row) {
    const description = String(row.description || '').trim();
    const priceText = String(row.price || '').trim();
    const sortOrderText = String(row.sort_order || '').trim();
    const parsedPrice = Number.parseFloat(priceText);
    const parsedSortOrder = sortOrderText ? Number.parseInt(sortOrderText, 10) : null;
    if (!description) {
      alert('Description is required');
      return;
    }
    if (!/^\d{1,4}(\.\d{1,2})?$/.test(priceText) || !Number.isFinite(parsedPrice)) {
      alert('Price must be up to 4 digits and 2 decimals (max 9999.99)');
      return;
    }
    if (parsedPrice < 0 || parsedPrice > 9999.99) {
      alert('Price must be between 0.00 and 9999.99');
      return;
    }
    if (sortOrderText && (!/^\d{1,2}$/.test(sortOrderText) || !Number.isInteger(parsedSortOrder))) {
      alert('Sort order must be up to 2 digits (00-99)');
      return;
    }
    if (parsedSortOrder !== null && (parsedSortOrder < 0 || parsedSortOrder > 99)) {
      alert('Sort order must be between 0 and 99');
      return;
    }

    setCoaSavingId(row.coa_id);
    try {
      const body = {
        description,
        price: parsedPrice,
        sort_order: parsedSortOrder
      };
      await axios.put(`/job-coas/${row.coa_id}`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadCoas();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update job COA');
    } finally {
      setCoaSavingId(null);
    }
  }

  async function handleDeleteCoa(coaId) {
    if (!window.confirm('Delete this COA?')) return;
    setCoaDeletingId(coaId);
    try {
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };
      await axios.delete(`/job-coas/${coaId}`, config);
      await loadCoas();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to delete job COA');
    } finally {
      setCoaDeletingId(null);
    }
  }

  async function handleAddCoa(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    const description = String(newCoa.description || '').trim();
    const priceText = String(newCoa.price || '').trim();
    const sortOrderText = String(newCoa.sort_order || '').trim();
    const parsedPrice = Number.parseFloat(priceText);
    const parsedSortOrder = sortOrderText ? Number.parseInt(sortOrderText, 10) : null;

    if (!description) {
      alert('Description is required');
      return;
    }
    if (!/^\d{1,4}(\.\d{1,2})?$/.test(priceText) || !Number.isFinite(parsedPrice)) {
      alert('Price must be up to 4 digits and 2 decimals (max 9999.99)');
      return;
    }
    if (parsedPrice < 0 || parsedPrice > 9999.99) {
      alert('Price must be between 0.00 and 9999.99');
      return;
    }
    if (sortOrderText && (!/^\d{1,2}$/.test(sortOrderText) || !Number.isInteger(parsedSortOrder))) {
      alert('Sort order must be up to 2 digits (00-99)');
      return;
    }
    if (parsedSortOrder !== null && (parsedSortOrder < 0 || parsedSortOrder > 99)) {
      alert('Sort order must be between 0 and 99');
      return;
    }

    setCoaCreating(true);
    try {
      const body = {
        description,
        price: parsedPrice,
        sort_order: parsedSortOrder
      };
      await axios.post('/job-coas', body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewCoa({ description: '', price: '', sort_order: '' });
      await loadCoas();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to create job COA');
    } finally {
      setCoaCreating(false);
    }
  }

  async function handleSaveProfile(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const composedAddress = composeAddress(addressLine, city, zip);
      const res = await axios.put(
        '/tenant/me/profile',
        {
        address: composedAddress || null,
        phone: profile.phone || null,
        email: profile.email || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const mergedProfile = { ...profile, ...(res.data?.profile || {}) };
      setProfile(mergedProfile);
      const parts = parseAddressParts(mergedProfile.address || composedAddress);
      setAddressLine(parts.addressLine);
      setCity(parts.city);
      setZip(parts.zip);
      alert('Contact information updated');
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  }

  async function handleStartStripeConnect() {
    if (stripeStarting) return;
    setStripeStarting(true);
    try {
      const res = await axios.post(
        '/stripe/connect/start',
        { returnPath: '/tenant-portal' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const url = String(res?.data?.url || '').trim();
      if (!url) {
        throw new Error('Missing Stripe onboarding URL');
      }
      window.location.assign(url);
    } catch (err) {
      alert(err?.response?.data?.error || err?.message || 'Failed to start Stripe Connect flow');
    } finally {
      setStripeStarting(false);
    }
  }

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    loadCoas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.id, canManageCoas]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripe = params.get('stripe');
    if (stripe === 'connected' || stripe === 'incomplete' || stripe === 'error') {
      loadProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  if (loading) return <div style={{ padding: 20 }}>Loading tenant profile...</div>;

  const stripeQueryStatus = String(new URLSearchParams(location.search).get('stripe') || '').trim().toLowerCase();
  const stripeStatusText = stripeQueryStatus === 'connected'
    ? 'Stripe account connected successfully.'
    : stripeQueryStatus === 'incomplete'
      ? 'Stripe onboarding is not complete yet. Continue setup below.'
      : stripeQueryStatus === 'error'
        ? 'Stripe onboarding hit an error. Please try again.'
        : '';

  return (
    <div style={{ padding: 20, maxWidth: 760, margin: '0 auto' }}>
      <h2>Tenant Portal</h2>
      <p style={{ marginTop: 0, marginBottom: 14 }}>Update your contact information and job COAs.</p>

      <div style={{ marginBottom: 12, border: '1px solid #dbe5e1', borderRadius: 8, padding: '10px 12px', background: '#fcfffe' }}>
        <div style={{ fontWeight: 700, color: '#1f5862' }}>Active Plan: Single Version</div>
        <div style={{ fontSize: 13, color: '#4b5563' }}>All core features are enabled for your tenant.</div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ ...portalInnerBoxStyle, marginTop: 10, alignItems: 'center', textAlign: 'center' }}>
          <h3 style={{ ...sectionTitleStyle, marginBottom: 0 }}>Stripe Payments</h3>
          <div style={{ color: CALENDAR_BLUE, fontSize: 14, lineHeight: 1.4, maxWidth: 620 }}>
            Use this secure link to access your Stripe account for onboarding, updates, and account management. Your tenant account reference is handled by the app and stays hidden.
          </div>
          {stripeStatusText ? <div style={{ fontSize: 13, color: '#1f2937' }}>{stripeStatusText}</div> : null}
          <button type="button" onClick={handleStartStripeConnect} disabled={stripeStarting} style={smallBlueButtonStyle}>
            {stripeStarting ? 'Opening Stripe...' : 'Connect with Stripe'}
          </button>
        </div>

        <form onSubmit={handleSaveProfile} style={{ display: 'grid', gap: 10 }}>
          <div style={portalInnerBoxStyle}>
            <h3 style={sectionTitleStyle}>Company Contact Information</h3>
            <div style={{ color: CALENDAR_BLUE, fontSize: 14, lineHeight: 1.35 }}>
              This contact information is used to populate customer communications from your company.
            </div>
            <div style={{ fontSize: 14, color: '#333', display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
              <span><strong>Name:</strong> {profile.name} ({profile.slug})</span>
            </div>
            <div style={{ borderTop: '1px solid #e5e7eb' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr 0.8fr 1.4fr 1fr', gap: 8 }}>
              <input
                type="text"
                placeholder="Address"
                value={addressLine}
                onChange={e => setAddressLine(e.target.value)}
                style={textInputStyle}
              />
              <input
                type="text"
                placeholder="City"
                value={city}
                onChange={e => setCity(e.target.value)}
                style={textInputStyle}
              />
              <input
                type="text"
                placeholder="Zip code"
                value={zip}
                onChange={e => setZip(e.target.value)}
                style={textInputStyle}
              />
              <input
                type="email"
                placeholder="Email"
                value={profile.email || ''}
                onChange={e => setProfile({ ...profile, email: e.target.value })}
                style={textInputStyle}
              />
              <input
                type="text"
                placeholder="Phone"
                value={profile.phone || ''}
                onChange={e => setProfile({ ...profile, phone: formatPhoneInput(e.target.value) })}
                style={textInputStyle}
                maxLength={14}
              />
            </div>
            <div style={{ textAlign: 'center' }}>
              <button type="submit" disabled={saving} style={smallBlueButtonStyle}>{saving ? 'Saving...' : 'Update Contact Info'}</button>
            </div>
          </div>
        </form>

        <div style={{ ...portalInnerBoxStyle, marginTop: 10 }}>
          <h3 style={sectionTitleStyle}>Job Chart of Accounts</h3>
          <div style={{ marginBottom: 10, color: CALENDAR_BLUE, fontSize: 14, lineHeight: 1.35 }}>
            COA (Chart of Accounts) entries define reusable job line items with a default cost and sort order.
            These values help keep pricing entry consistent and control display order in the app.
          </div>
          {canManageCoas ? (
            <>
              {coaError ? <div style={{ color: '#dc2626', marginBottom: 8 }}>{coaError}</div> : null}
              {coaLoading ? <div style={{ marginBottom: 8 }}>Loading COAs...</div> : null}

              <div style={{ display: 'grid', gridTemplateColumns: '52px 1.8fr 1fr 1fr', gap: 8, alignItems: 'end', fontSize: 12, color: '#4b5563', fontWeight: 700, padding: '0 4px' }}>
                <div>Select</div>
                <div>Description</div>
                <div>Cost</div>
                <div>Sort Order</div>
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                {coaRows.map(row => (
                  <div key={row.coa_id} style={{ display: 'grid', gridTemplateColumns: '52px 1.8fr 1fr 1fr', gap: 8, alignItems: 'center', padding: '6px 8px', borderRadius: 6, background: String(selectedCoaId) === String(row.coa_id) ? '#f4fbf9' : '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <input
                        type="radio"
                        name="selected-coa"
                        checked={String(selectedCoaId) === String(row.coa_id)}
                        onChange={() => setSelectedCoaId(String(row.coa_id))}
                        style={{ accentColor: CALENDAR_BLUE, width: 16, height: 16, cursor: 'pointer' }}
                        aria-label={`Select COA ${row.description || row.coa_id}`}
                      />
                    </div>
                    <input
                      type="text"
                      value={row.description}
                      onChange={e => handleCoaFieldChange(row.coa_id, 'description', e.target.value)}
                      placeholder="Description"
                      aria-label="COA description"
                    />
                    <input
                      type="number"
                      min="0"
                      max="9999.99"
                      step="0.01"
                      value={row.price}
                      onChange={e => handleCoaFieldChange(row.coa_id, 'price', e.target.value)}
                      placeholder="0000.00"
                      aria-label="COA cost"
                    />
                    <input
                      type="number"
                      min="0"
                      max="99"
                      step="1"
                      value={row.sort_order}
                      onChange={e => handleCoaFieldChange(row.coa_id, 'sort_order', e.target.value)}
                      placeholder="00"
                      aria-label="COA sort order"
                    />
                  </div>
                ))}
              </div>

              <form onSubmit={handleAddCoa} style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '52px 1.8fr 1fr 1fr', gap: 8, alignItems: 'end' }}>
                <div style={{ fontSize: 12, color: '#4b5563', textAlign: 'center', fontWeight: 700 }}>New</div>
                <input
                  type="text"
                  value={newCoa.description}
                  onChange={e => setNewCoa({ ...newCoa, description: e.target.value })}
                  placeholder="New description"
                  required
                  aria-label="New COA description"
                />
                <input
                  type="number"
                  min="0"
                  max="9999.99"
                  step="0.01"
                  value={newCoa.price}
                  onChange={e => setNewCoa({ ...newCoa, price: e.target.value })}
                  placeholder="0000.00"
                  required
                  aria-label="New COA cost"
                />
                <input
                  type="number"
                  min="0"
                  max="99"
                  step="1"
                  value={newCoa.sort_order}
                  onChange={e => setNewCoa({ ...newCoa, sort_order: e.target.value })}
                  placeholder="00"
                  aria-label="New COA sort order"
                />
              </form>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 10 }}>
                <button type="button" style={smallBlueButtonStyle} disabled={coaCreating} onClick={handleAddCoa}>{coaCreating ? 'Adding...' : 'Add'}</button>
                <button
                  type="button"
                  style={smallBlueButtonStyle}
                  disabled={!selectedCoaId || coaSavingId !== null}
                  onClick={() => {
                    const row = coaRows.find(item => String(item.coa_id) === String(selectedCoaId));
                    if (row) {
                      handleSaveCoa(row);
                    }
                  }}
                >
                  {coaSavingId ? 'Updating...' : 'Update'}
                </button>
                <button
                  type="button"
                  style={{ ...smallBlueButtonStyle, background: '#b91c1c' }}
                  disabled={!selectedCoaId || coaDeletingId !== null}
                  onClick={() => handleDeleteCoa(selectedCoaId)}
                >
                  {coaDeletingId ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ fontSize: 12, color: '#4b5563' }}>Job COAs are editable by Office/Admin users.</div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TenantPortal;
